import type { RepeatingSectionRowData } from '@/features/AI-documents/types/docGen.types';
import { useSessionStore } from '@/shared/stores/sessionStore';
import {
  buildOfficerAppointmentContactDataFromFlat,
  enrichOfficerAppointmentSeedFormData,
} from './adaptOfficerAppointmentEfilingFormData';
import {
  applyContactDataToDirectorStructuredProfile,
  formatDirectorStructuredAddress,
} from './directorStructuredProfile';
import { nationalityLabelForOutput } from '@/features/startTask/preset_task/sg/composables/cmBaseCodeOptions';
import { resolveForm45UsesPassport, toForm45ConditionalFlag } from './form45Conditionals';

export interface AppointDirectorFormModel {
  companyName: string;
  companyRegistrationNo: string;
  appointmentEffectiveDate: string;
  /** Form 45 — [[if has_pre_1993_history]] (iv) 항목 표시 */
  hasPre1993History: boolean;
  /** Form 45 — [[if is_employment_pass_holder]] (bb) 항목 표시 */
  isEmploymentPassHolder: boolean;
  /** Form 45 — [[if needs_interpreter]] / [[else]] (c) 분기 */
  needsInterpreter: boolean;
  /** Form 45 — interpreter NRIC (통역 사용 시) */
  interpreterNric: string;
  stateLanguageOrDialect: string;
  stateName: string;
  directorName: string;
  directorAddress: string;
  directorAlternateAddress: string;
  directorIdentificationNo: string;
  directorNationality: string;
  directorDateOfBirth: string;
  directorEmail: string;
  directorContactNo: string;
  directorIdTypeCd: string;
  directorMobileCountryCd: string;
  directorMobileAreaCode: string;
  directorAddressTypeCd: string;
  directorPostalCode: string;
  directorBlockNo: string;
  directorStreetName: string;
  directorBuildingName: string;
  directorAddrLevel: string;
  directorAddrUnit: string;
  directorForeignLine1: string;
  directorForeignLine2: string;
  directorAltAddressTypeCd: string;
  directorAltPostalCode: string;
  directorAltBlockNo: string;
  directorAltStreetName: string;
  directorAltBuildingName: string;
  directorAltAddrLevel: string;
  directorAltAddrUnit: string;
  directorAltForeignLine1: string;
  directorAltForeignLine2: string;
  secretaryFirmName: string;
  resolutionDate: string;
  executionDate: string;
  repeatingData: Record<string, RepeatingSectionRowData[]>;
  contactCd: string;
  positionCd: string;
  contactData: Record<string, any> | null;
}

export function createEmptyAppointDirectorForm(): AppointDirectorFormModel {
  return {
    companyName: '',
    companyRegistrationNo: '',
    appointmentEffectiveDate: '',
    hasPre1993History: false,
    isEmploymentPassHolder: false,
    needsInterpreter: false,
    interpreterNric: '',
    stateLanguageOrDialect: '',
    stateName: '',
    directorName: '',
    directorAddress: '',
    directorAlternateAddress: '',
    directorIdentificationNo: '',
    directorNationality: '',
    directorDateOfBirth: '',
    directorEmail: '',
    directorContactNo: '',
    directorIdTypeCd: '',
    directorMobileCountryCd: '',
    directorMobileAreaCode: '',
    directorAddressTypeCd: 'B07001',
    directorPostalCode: '',
    directorBlockNo: '',
    directorStreetName: '',
    directorBuildingName: '',
    directorAddrLevel: '',
    directorAddrUnit: '',
    directorForeignLine1: '',
    directorForeignLine2: '',
    directorAltAddressTypeCd: 'B07001',
    directorAltPostalCode: '',
    directorAltBlockNo: '',
    directorAltStreetName: '',
    directorAltBuildingName: '',
    directorAltAddrLevel: '',
    directorAltAddrUnit: '',
    directorAltForeignLine1: '',
    directorAltForeignLine2: '',
    secretaryFirmName: '',
    resolutionDate: '',
    executionDate: '',
    repeatingData: {},
    contactCd: '',
    positionCd: 'B30027',
    contactData: null,
  };
}

function trim(v: unknown): string {
  return v === null || v === undefined ? '' : String(v).trim();
}

/** LETTAGA 내부 entity PK(예: EB3F4D31-… GUID) — ACRA EntityUEN / FilingAgentUEN 으로 쓰면 안 됨 */
function looksLikeUuid(s: string): boolean {
  if (!s) return false;
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s.trim());
}

/** E-Filing JSON 용 EntityUEN: 회사 등록번호 우선, 내부 GUID(entityCd)는 제외 */
function resolveEntityUenForFiling(model: AppointDirectorFormModel, entityCd: string): string {
  const companyUen = trim(model.companyRegistrationNo);
  const ec = trim(entityCd);
  if (companyUen) return companyUen;
  if (ec && !looksLikeUuid(ec)) return ec;
  return '';
}

/** ACRA FilingAgentUEN — 로그인 고객(파일링 에이전트) 사업자 등록번호 (OfficerAppointmentForm 과 동일) */
function resolveFilingAgentUenForFiling(): string {
  try {
    const sessionStore = useSessionStore();
    return trim(sessionStore.getCustomerInfo()?.regNo || '');
  } catch {
    return '';
  }
}

/** 해외 주소 줄이 있으면 루트 directorAddress 는 줄 합친 값만 (한 줄 전체 + 줄 분리 중복 방지) */
function joinForeignAddressLines(cd: Record<string, any> | null | undefined): string {
  if (!cd || typeof cd !== 'object') return '';
  const f1 = trim(cd.FOREIGN_ADDRESS_LINE_1 ?? cd.foreignAddressLine1);
  const f2 = trim(cd.FOREIGN_ADDRESS_LINE_2 ?? cd.foreignAddressLine2);
  if (!f1 && !f2) return '';
  return [f1, f2].filter(Boolean).join(', ').trim();
}

function joinAltForeignAddressLines(cd: Record<string, any> | null | undefined): string {
  if (!cd || typeof cd !== 'object') return '';
  const f1 = trim(cd.ALT_FOREIGN_ADDRESS_LINE_1 ?? cd.altForeignAddressLine1);
  const f2 = trim(cd.ALT_FOREIGN_ADDRESS_LINE_2 ?? cd.altForeignAddressLine2);
  if (!f1 && !f2) return '';
  return [f1, f2].filter(Boolean).join(', ').trim();
}

function normalizeRepeatingData(
  repeatingData: Record<string, RepeatingSectionRowData[]> | null | undefined,
): Record<string, RepeatingSectionRowData[]> {
  const out: Record<string, RepeatingSectionRowData[]> = {};
  if (!repeatingData || typeof repeatingData !== 'object') return out;

  for (const [sectionName, rows] of Object.entries(repeatingData)) {
    if (!Array.isArray(rows)) continue;
    const cleanRows = rows
      .filter((row) => row && typeof row === 'object' && !Array.isArray(row))
      .map((row) => {
        const cleanRow: RepeatingSectionRowData = {};
        for (const [key, value] of Object.entries(row)) {
          cleanRow[key] = trim(value);
        }
        return cleanRow;
      });
      if (cleanRows.length > 0) out[sectionName] = cleanRows;
  }

  return out;
}

function getExecutionDateParts(iso: string): { day: string; month: string; year: string } {
  const s = trim(iso);
  if (!s) return { day: '', month: '', year: '' };
  const matched = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (!matched) return { day: '', month: '', year: '' };
  const d = new Date(Number(matched[1]), Number(matched[2]) - 1, Number(matched[3]), 12, 0, 0);
  if (isNaN(d.getTime())) return { day: '', month: '', year: '' };
  return {
    day: String(d.getDate()),
    month: d.toLocaleString('en-US', { month: 'long' }),
    year: String(d.getFullYear()),
  };
}

export function buildForm45ConditionalTemplateVariables(
  model: AppointDirectorFormModel,
): Record<string, unknown> {
  const hasPre1993History = !!model.hasPre1993History;
  const isEmploymentPassHolder = !!model.isEmploymentPassHolder;
  const needsInterpreter = !!model.needsInterpreter;
  const usesPassport = resolveForm45UsesPassport({ directorIdTypeCd: model.directorIdTypeCd });

  return {
    has_pre_1993_history: hasPre1993History,
    is_employment_pass_holder: isEmploymentPassHolder,
    needs_interpreter: needsInterpreter,
    uses_passport: usesPassport,
    interpreter_language: trim(model.stateLanguageOrDialect),
    interpreter_name: trim(model.stateName),
    interpreter_nric: trim(model.interpreterNric),
  };
}

export function toAppointDirectorTemplateVariables(model: AppointDirectorFormModel): Record<string, any> {
  const repeatingData = normalizeRepeatingData(model.repeatingData);
  const execution = getExecutionDateParts(model.executionDate);
  const cd = model.contactData as Record<string, any> | null | undefined;
  const addrType = trim(cd?.ADDRESS_TYPE_CD ?? cd?.addressTypeCd);
  const altType = trim(cd?.ALTERNATE_ADDRESS_CD ?? cd?.alternateAddressCd);
  const primaryFj = joinForeignAddressLines(cd);
  const altFj = joinAltForeignAddressLines(cd);
  const docDirectorAddr =
    addrType === 'B07002' && primaryFj ? primaryFj : trim(model.directorAddress);
  const docDirectorAlt =
    (altType === 'B07002' && altFj) || (!altType && altFj) ? altFj : trim(model.directorAlternateAddress);

  return {
    company_name: trim(model.companyName),
    company_registration_no: trim(model.companyRegistrationNo),
    appointment_effective_date: trim(model.appointmentEffectiveDate),
    state_language_or_dialect: trim(model.stateLanguageOrDialect),
    state_name: trim(model.stateName),
    director_name: trim(model.directorName),
    director_address: docDirectorAddr,
    director_alternate_address: docDirectorAlt,
    director_identification_no: trim(model.directorIdentificationNo),
    director_nationality: nationalityLabelForOutput(trim(model.directorNationality)),
    director_date_of_birth: trim(model.directorDateOfBirth),
    director_email: trim(model.directorEmail),
    director_contact_no: trim(model.directorContactNo),
    secretary_firm_name: trim(model.secretaryFirmName),
    resolution_date: trim(model.resolutionDate),
    execution_day: execution.day,
    execution_month: execution.month,
    execution_year: execution.year,
    ...buildForm45ConditionalTemplateVariables(model),
    repeatingData,
    ...repeatingData,
    signers: '',
    Signers: '',
  };
}

export function buildOfficerAppointmentSeedFormData(
  model: AppointDirectorFormModel,
  entityCd: string,
): Record<string, any> {
  const ec = trim(entityCd);
  const fd = buildAppointDirectorTaskFormData(model, ec);
  const entityUEN = resolveEntityUenForFiling(model, ec);
  const filingAgentUEN = resolveFilingAgentUenForFiling();
  const contactCd = trim(fd.contactCd || fd.officerNameCd || '');

  let contactData = fd.contactData;
  if (!contactCd) {
    const built = buildOfficerAppointmentContactDataFromFlat({
      ...fd,
      ...toAppointDirectorTemplateVariables(model),
    }) as Record<string, any>;
    if (trim((built as any).CONTACT_NAME)) {
      contactData = built;
    }
  }

  return enrichOfficerAppointmentSeedFormData({
    ...fd,
    ...toAppointDirectorTemplateVariables(model),
    contactData,
    appointedDate: { value: trim(fd.appointmentEffectiveDate || fd.appointedDate || '') },
    officerNameCd: contactCd,
    entityUEN,
    filingAgentUEN,
  });
}

export function applyDirectorStructuredProfileFromContactData(
  model: AppointDirectorFormModel,
  contactData: Record<string, unknown> | null | undefined,
): void {
  const profile = applyContactDataToDirectorStructuredProfile(contactData);
  model.directorIdTypeCd = profile.idTypeCd;
  model.directorMobileCountryCd = profile.mobileCountryCd;
  model.directorMobileAreaCode = profile.mobileAreaCode;
  model.directorAddress = formatDirectorStructuredAddress(profile.primary);
  model.directorAlternateAddress = formatDirectorStructuredAddress(profile.alternate);
}

export function buildFlatContactSourceFromModel(model: AppointDirectorFormModel): Record<string, unknown> {
  return {
    directorName: model.directorName,
    directorIdentificationNo: model.directorIdentificationNo,
    directorNationality: model.directorNationality,
    directorDateOfBirth: model.directorDateOfBirth,
    directorEmail: model.directorEmail,
    directorContactNo: model.directorContactNo,
    directorAddress: model.directorAddress,
    directorAlternateAddress: model.directorAlternateAddress,
    directorIdTypeCd: model.directorIdTypeCd,
    directorMobileCountryCd: model.directorMobileCountryCd,
    directorMobileAreaCode: model.directorMobileAreaCode,
    contactCd: model.contactCd,
    repeatingData: model.repeatingData,
  };
}

/** 수동 입력 → e-Filing contactData (합쳐진 주소 문자열을 역분해) */
export function resolveAppointDirectorContactData(
  model: AppointDirectorFormModel,
): Record<string, any> | null {
  const flat = buildFlatContactSourceFromModel(model);
  const built = buildOfficerAppointmentContactDataFromFlat(flat) as Record<string, any>;
  if (!trim(built.CONTACT_NAME)) return null;
  const contactCd = trim(model.contactCd);
  if (contactCd) {
    built.contactCd = contactCd;
    built.officerNameCd = contactCd;
  }
  return built;
}

/** Create/Update 직전 formModel.contactData 반영 (presetForm 복원용) */
export function syncAppointDirectorManualContactData(model: AppointDirectorFormModel): void {
  model.contactData = resolveAppointDirectorContactData(model);
}

export function buildAppointDirectorTaskFormData(
  model: AppointDirectorFormModel,
  entityCd: string,
): Record<string, any> {
  const contactData = resolveAppointDirectorContactData(model);
  const contactCd = trim(model.contactCd) || trim(contactData?.contactCd);
  const directorName =
    trim(model.directorName) ||
    trim(contactData?.contactName) ||
    [trim(contactData?.firstName), trim(contactData?.middleName), trim(contactData?.surname)]
      .filter(Boolean)
      .join(' ')
      .trim();
  const appointmentDate = trim(model.appointmentEffectiveDate);
  const entityName = trim(model.companyName);
  const entityRegNo = trim(model.companyRegistrationNo);

  const addrType = trim((contactData as any)?.ADDRESS_TYPE_CD || (contactData as any)?.addressTypeCd);
  const altType = trim((contactData as any)?.ALTERNATE_ADDRESS_CD || (contactData as any)?.alternateAddressCd);
  const primaryForeignJoined = joinForeignAddressLines(contactData);
  const altForeignJoined = joinAltForeignAddressLines(contactData);
  const directorAddressOut =
    addrType === 'B07002' && primaryForeignJoined ? primaryForeignJoined : trim(model.directorAddress);
  const directorAlternateOut =
    (altType === 'B07002' && altForeignJoined) || (!altType && altForeignJoined)
      ? altForeignJoined
      : trim(model.directorAlternateAddress);

  return {
    entity: trim(entityCd),
    entityCd: trim(entityCd),
    entityName: { value: entityName },
    entityRegNo: { value: entityRegNo },
    companyName: entityName,
    companyRegistrationNo: entityRegNo,
    officerNameCd: contactCd,
    officerName: directorName,
    officerCode: contactCd,
    contactCd,
    position: trim(model.positionCd) || 'B30027',
    positionCd: trim(model.positionCd) || 'B30027',
    positionText: 'Director',
    appointedDate: appointmentDate,
    appointmentDate,
    appointmentEffectiveDate: appointmentDate,
    directorName,
    directorIdentificationNo: trim(model.directorIdentificationNo) || trim(contactData?.contactRegNo),
    directorNationality: nationalityLabelForOutput(trim(model.directorNationality)) || trim(contactData?.nationalityText),
    directorDateOfBirth: trim(model.directorDateOfBirth) || trim(contactData?.dateOfBirth),
    directorEmail: trim(model.directorEmail) || trim(contactData?.contactEmail),
    directorContactNo: trim(model.directorContactNo) || trim(contactData?.mobileNo),
    directorIdTypeCd: trim(model.directorIdTypeCd) || trim((contactData as any)?.ID_TYPE_CD || (contactData as any)?.idTypeCd),
    directorMobileCountryCd: trim(model.directorMobileCountryCd) || trim((contactData as any)?.COUNTRY_CD || (contactData as any)?.countryCd),
    directorMobileAreaCode: trim(model.directorMobileAreaCode) || trim((contactData as any)?.AREA_CODE || (contactData as any)?.areaCode),
    directorAddress: directorAddressOut,
    directorAlternateAddress: directorAlternateOut,
    resolutionDate: trim(model.resolutionDate),
    executionDate: trim(model.executionDate),
    hasPre1993History: !!model.hasPre1993History,
    isEmploymentPassHolder: !!model.isEmploymentPassHolder,
    needsInterpreter: !!model.needsInterpreter,
    interpreterNric: trim(model.interpreterNric),
    stateLanguageOrDialect: trim(model.stateLanguageOrDialect),
    stateName: trim(model.stateName),
    ...buildForm45ConditionalTemplateVariables(model),
    contactData,
  };
}

export function isAppointDirectorFormComplete(model: AppointDirectorFormModel): boolean {
  const baseComplete = [
    model.companyName,
    model.companyRegistrationNo,
    model.appointmentEffectiveDate,
    model.directorName,
    model.directorIdentificationNo,
    model.directorNationality,
    model.directorDateOfBirth,
    model.directorEmail,
    model.directorContactNo,
    model.directorMobileCountryCd,
    model.secretaryFirmName,
    model.resolutionDate,
    model.executionDate,
  ].every((v) => trim(v).length > 0)
    && trim(model.directorAddress).length > 0;

  if (!baseComplete) return false;

  if (!model.needsInterpreter) return true;

  return [
    model.stateLanguageOrDialect,
    model.stateName,
    model.interpreterNric,
  ].every((v) => trim(v).length > 0);
}

/** Edit 복원 — 구 presetForm / Yes·No 문자열 호환 */
export function normalizeAppointDirectorFormConditionals(model: AppointDirectorFormModel): void {
  model.hasPre1993History = toForm45ConditionalFlag(model.hasPre1993History);
  model.isEmploymentPassHolder = toForm45ConditionalFlag(model.isEmploymentPassHolder);
  model.needsInterpreter = toForm45ConditionalFlag(model.needsInterpreter);
}
